/**
 * SPDX-License-Identifier: MPL-2.0
 * Copyright 2024 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2025-2026 (c) Miriam Mack, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import Api, { type Company } from 'Api/Api';
import { useAuth } from 'Contexts/Auth';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { BsPersonFillGear } from 'react-icons/bs';
import { FeedbackAlert, useFeedback } from '../components/Feedback';
import { passwordError } from '../components/password';
import RoleBadge from '../components/RoleBadges';
import styles from './Me.module.css';

const effectiveRole = (roles: string[] | null | undefined) => {
  if (roles?.includes('SuperAdmin')) return 'SuperAdmin';
  if (roles?.includes('Admin')) return 'Admin';
  return 'CompanyMember';
};

type CompanyTagsProps = {
  companyIds: string[] | null | undefined;
  companies: Company[] | null;
};

const CompanyTags = ({ companyIds, companies }: CompanyTagsProps) => {
  if (!companyIds || companyIds.length === 0) return <span className="text-muted">None</span>;
  if (companies === null) return <span className="text-muted">The companies could not be loaded.</span>;
  return (
    <div className={styles['tag-list']}>
      {companyIds.map((id) => (
        <span key={id} className={styles['company-tag']}>
          {companies.find((c) => c.Id === id)?.Name ?? 'Unknown company'}
        </span>
      ))}
    </div>
  );
};

const ChangePassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { feedback, success, error, clear } = useFeedback();

  const lengthError = passwordError(newPassword);
  const matchError = confirmPassword === newPassword ? null : 'The passwords do not match.';
  const isValid = !lengthError && !matchError;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    clear();
    if (!isValid) return;
    setSubmitting(true);
    try {
      await Api.Me.changeSelf(newPassword);
      setNewPassword('');
      setConfirmPassword('');
      setSubmitted(false);
      success('Your password has been changed.');
    } catch (err) {
      error(err, 'The password could not be changed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={styles.panel} aria-labelledby="change-password-heading">
      <h3 id="change-password-heading" className={styles['panel-header']}>
        Change password
      </h3>
      <div className={styles['panel-body']}>
        <Form className={styles['password-form']} onSubmit={handleSubmit} noValidate>
          <Form.Group className="mb-3" controlId="new-password">
            <Form.Label>New password</Form.Label>
            <Form.Control
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              isInvalid={submitted && !!lengthError}
            />
            <Form.Control.Feedback type="invalid">{lengthError}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3" controlId="confirm-password">
            <Form.Label>Confirm new password</Form.Label>
            <Form.Control
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              isInvalid={(submitted || confirmPassword.length > 0) && !!matchError}
            />
            <Form.Control.Feedback type="invalid">{matchError}</Form.Control.Feedback>
          </Form.Group>

          <FeedbackAlert feedback={feedback} />

          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? 'Saving…' : 'Change password'}
          </Button>
        </Form>
      </div>
    </section>
  );
};

const Me = () => {
  const auth = useAuth();
  const [companies, setCompanies] = useState<Company[] | null>([]);

  useEffect(() => {
    Api.Company.getCompanies()
      .then((res) => setCompanies(res.data))
      .catch(() => setCompanies(null));
  }, []);

  if (auth.authed === undefined) {
    return <div className="text-muted p-4">Loading account data…</div>;
  }

  const user = auth.user;
  if (!auth.authed || !user) return null;

  return (
    <div className={styles['me-page']}>
      <h2 className={`header ${styles['page-title']}`}>
        <BsPersonFillGear className="icon" />
        My Account
      </h2>

      <section className={styles.panel} aria-labelledby="account-heading">
        <h3 id="account-heading" className={styles['panel-header']}>
          Account
        </h3>
        <div className={styles['panel-body']}>
          <dl className={styles.details}>
            <div className={styles['details-row']}>
              <dt>Username</dt>
              <dd>{user.Username}</dd>
            </div>
            <div className={styles['details-row']}>
              <dt>Role</dt>
              <dd>
                <RoleBadge role={effectiveRole(user.Roles)} />
              </dd>
            </div>
            <div className={styles['details-row']}>
              <dt>Companies</dt>
              <dd>
                <CompanyTags companyIds={user.CompanyIds} companies={companies} />
              </dd>
            </div>
            <div className={styles['details-row']}>
              <dt>User ID</dt>
              <dd className="text-muted">{user.Id}</dd>
            </div>
          </dl>
        </div>
      </section>

      <ChangePassword />
    </div>
  );
};

export default Me;
